// 📦 pages/CartPage.jsx
// 📝 Client Note: Cart page fully updated with styled header, better UX, trash button,
// empty state improvements, and dynamic product recommendations.

import React, { useEffect, useState } from "react";
import { shopifyAPI } from "../utils/shopify";
import { useCart } from "../context/CartContext";
import { Trash2 } from "lucide-react";

const formatMoney = (amount, code = "USD") =>
  `${Number.parseFloat(amount || 0).toFixed(2)} ${code}`;

const CartPage = () => {
  const { cart, loading, fetchCart, updateCartItem, removeCartItem } = useCart();
  const [recommendations, setRecommendations] = useState([]);

  // ✅ Fetch cart on mount using stored cartId
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // 📝 Dynamic recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      const lastViewed = localStorage.getItem("last_viewed_product_id");

      if (lastViewed) {
        const query = `
          query productRecommendations($productId: ID!) {
            productRecommendations(productId: $productId) {
              id
              title
              handle
              featuredImage {
                url
                altText
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        `;
        const res = await shopifyAPI(query, { productId: lastViewed });
        setRecommendations(res.productRecommendations || []);
      } else {
        const query = `
          {
            products(first: 4) {
              edges {
                node {
                  id
                  title
                  handle
                  featuredImage {
                    url
                    altText
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        `;
        const res = await shopifyAPI(query);
        setRecommendations(res.products.edges.map((e) => e.node));
      }
    };
    fetchRecommendations();
  }, []);

  const handleQtyChange = async (lineId, newQty) => {
    if (newQty < 1) return;
    await updateCartItem(lineId, newQty);
  };

  const handleRemove = async (lineId) => {
    await removeCartItem(lineId);
  };

  const handleCheckout = (e) => {
    if (e) e.preventDefault();
    if (!cart?.checkoutUrl) return;
    window.location.href = cart.checkoutUrl;
  };

  if (loading) return <div className="p-6 text-center">Loading cart…</div>;

  const lines = cart?.lines?.edges || [];

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* 📝 Updated header */}
      <h1 className="text-5xl font-extrabold mb-8 pt-10 tracking-tight">
  <span className="text-black">Your</span>{" "}
  <span className="text-brandred">Cart</span>
</h1>

      {!lines.length ? (
        <>
          {/* Empty state */}
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-gray-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-4a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800">Your cart is empty</h2>
            <p className="text-gray-500 mt-1">Looks like you haven’t added anything yet.</p>
            <button
              onClick={() => (window.location.href = "/shop")}
              className="mt-6 bg-brandred text-white px-6 py-3 rounded-lg font-bold hover:bg-red-600 transition"
            >
              Shop Now
            </button>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">
                You may also like
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {recommendations.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow hover:shadow-lg transition p-4 flex flex-col"
                  >
                    <img
                      src={product.featuredImage?.url}
                      alt={product.featuredImage?.altText || product.title}
                      className="w-full h-40 object-cover rounded-md mb-3"
                    />
                    <h4 className="font-semibold text-gray-800">
                      {product.title}
                    </h4>
                    <p className="text-gray-600 text-sm mb-3">
                      {formatMoney(
                        product.priceRange.minVariantPrice.amount,
                        product.priceRange.minVariantPrice.currencyCode
                      )}
                    </p>
                    <a
                      href={`/product/${product.handle}`}
                      className="mt-auto bg-gray-900 text-white text-sm py-2 px-3 rounded hover:bg-gray-700 text-center"
                    >
                      View Product
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-6">
            {lines.map(({ node }) => {
              const product = node.merchandise?.product;
              const unitAmount =
                node.cost?.amountPerQuantity?.amount ??
                node.merchandise?.priceV2?.amount ??
                0;
              const unitCode =
                node.cost?.amountPerQuantity?.currencyCode ??
                node.merchandise?.priceV2?.currencyCode ??
                "USD";
              const lineTotal =
                node.cost?.subtotalAmount?.amount ??
                (Number(unitAmount) * Number(node.quantity) || 0);
              const lineCode =
                node.cost?.subtotalAmount?.currencyCode ?? unitCode;

              const image =
                product?.images?.edges?.[0]?.node?.url || "/placeholder.png";

              return (
                <div
                  key={node.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between bg-white shadow-md rounded-lg p-4"
                >
                  {/* Left */}
                  <div className="flex items-center gap-4 mb-4 sm:mb-0">
                    <img
                      src={image}
                      alt={product?.title || "Product"}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {product?.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {node.merchandise?.title}
                      </p>
                      <p className="text-sm text-gray-600">
                        Price: {formatMoney(unitAmount, unitCode)}
                      </p>
                    </div>
                  </div>

                  {/* Right controls */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Quantity */}
                    <div className="flex items-center border rounded">
                      <button
                        onClick={() =>
                          handleQtyChange(node.id, Number(node.quantity) - 1)
                        }
                        className="px-2 py-1"
                      >
                        –
                      </button>
                      <span className="px-3">{node.quantity}</span>
                      <button
                        onClick={() =>
                          handleQtyChange(node.id, Number(node.quantity) + 1)
                        }
                        className="px-2 py-1"
                      >
                        +
                      </button>
                    </div>

                    {/* Line Total */}
                    <span className="font-semibold text-gray-900">
                      {formatMoney(lineTotal, lineCode)}
                    </span>

                    {/* 🚮 Trash button */}
                    <button
                      onClick={() => handleRemove(node.id)}
                      className="ml-2 text-red-500 hover:text-red-700"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="bg-white shadow-md rounded-lg p-6 h-fit">
            <h2 className="text-xl font-bold mb-4">Summary</h2>
            <p className="text-gray-700 mb-2">
              Subtotal:{" "}
              <span className="font-semibold text-red-600">
                {formatMoney(
                  cart?.estimatedCost?.subtotalAmount?.amount || 0,
                  cart?.estimatedCost?.subtotalAmount?.currencyCode || "USD"
                )}
              </span>
            </p>
            <p className="text-gray-500 mb-6 text-sm">
              Taxes and shipping calculated at checkout.
            </p>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={!cart?.checkoutUrl}
              className={`w-full block text-center py-3 rounded-lg font-bold transition ${
                cart?.checkoutUrl
                  ? "bg-brandred text-white hover:bg-red-600 cursor-pointer"
                  : "bg-gray-400 text-white cursor-not-allowed"
              }`}
            >
              Proceed to Checkout →
            </button>
            <button
              onClick={() => (window.location.href = "/shop")}
              className="w-full mt-3 border py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-100"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
